/**
 * Transcript Operations Utilities
 * 
 * Core operations for manipulating transcript blocks
 */

import { createWordBoundaryRegex, createSimpleRegex } from './regexUtils';
import { syncWordsWithText } from './wordTimestampSync';

/**
 * Generate unique ID for new blocks
 * @returns {string} Unique ID
 */
export const generateId = () => {
  return `block-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Split a block at cursor position
 * @param {Object} block - Block to split
 * @param {number} cursorPosition - Position in text where to split
 * @returns {Array} Array of two new blocks
 */
export const splitBlock = (block, cursorPosition) => {
  const textBefore = block.text.substring(0, cursorPosition).trim();
  const textAfter = block.text.substring(cursorPosition).trim();

  // Calculate middle time for split (will be updated if words array exists)
  let midTime = (block.start + block.end) / 2;

  // Split words array if it exists
  let wordsBefore = undefined;
  let wordsAfter = undefined;
  
  if (block.words && Array.isArray(block.words) && block.words.length > 0) {
    // Reconstruct text from words array to verify it matches block.text
    const reconstructedText = block.words
      .map((word, idx) => {
        const wordText = (word.word || word.raw || '').trim();
        return idx === 0 ? wordText : ` ${wordText}`;
      })
      .join('');
    
    // Normalize both texts for comparison (remove extra spaces)
    const normalizedBlockText = block.text.replace(/\s+/g, ' ').trim();
    const normalizedReconstructedText = reconstructedText.replace(/\s+/g, ' ').trim();
    
    // Only split words array if it matches block.text
    // If they don't match, words array is out of sync and we shouldn't split it
    if (normalizedBlockText === normalizedReconstructedText || 
        normalizedBlockText.length === 0 || 
        Math.abs(normalizedBlockText.length - normalizedReconstructedText.length) < 3) {
      
      // Find the split point in words array based on text position
      // We need to find which word contains the cursor position
      let charCount = 0;
      let wordSplitIndex = block.words.length; // Default to end if cursor is at the end
      
      for (let i = 0; i < block.words.length; i++) {
        const word = block.words[i];
        const wordText = (word.word || word.raw || '').trim();
        const wordStart = charCount;
        const wordEnd = charCount + wordText.length;
        
        // Check if cursor is within this word or right after it
        // If cursor is at the start of a word, split before that word
        // If cursor is in the middle or end of a word, split after that word
        if (cursorPosition <= wordStart) {
          // Cursor is before this word, split before it
          wordSplitIndex = i;
          break;
        } else if (cursorPosition <= wordEnd) {
          // Cursor is within this word
          // Split after this word (include it in first block)
          wordSplitIndex = i + 1;
          break;
        }
        
        // Move to next word (add space)
        charCount = wordEnd + 1;
      }
      
      // Ensure wordSplitIndex is valid
      wordSplitIndex = Math.max(0, Math.min(wordSplitIndex, block.words.length));
      
      // Split words array
      wordsBefore = block.words.slice(0, wordSplitIndex);
      wordsAfter = block.words.slice(wordSplitIndex);
      
      // Update end time for first block based on last word
      if (wordsBefore.length > 0) {
        const lastWord = wordsBefore[wordsBefore.length - 1];
        if (lastWord.end) {
          midTime = lastWord.end;
        }
      }
      
      // Update start time for second block based on first word
      if (wordsAfter.length > 0) {
        const firstWord = wordsAfter[0];
        if (firstWord.start) {
          midTime = firstWord.start;
        }
      }
    } else {
      // Words array doesn't match block.text - don't split it
      // This can happen if user edited text but words array wasn't updated
    }
  }

  const firstBlock = {
    ...block,
    text: textBefore,
    end: midTime,
    words: wordsBefore && wordsBefore.length > 0 ? wordsBefore : undefined,
    metadata: {
      ...block.metadata,
      isEdited: true,
      lastModified: new Date().toISOString(),
    },
  };

  const secondBlock = {
    ...block,
    id: generateId(),
    text: textAfter,
    start: midTime,
    words: wordsAfter && wordsAfter.length > 0 ? wordsAfter : undefined,
    // Keep same speaker by default - user can change it via dropdown
    speaker: block.speaker,
    metadata: {
      ...block.metadata,
      isEdited: true,
      lastModified: new Date().toISOString(),
    },
  };

  return [firstBlock, secondBlock];
};

/**
 * Find word split index for a given character position.
 * @param {Object} block - Block with words array
 * @param {number} charPosition - Character position in block.text
 * @param {'before'|'after'} mode - 'before': if charPosition is mid-word, word goes to NEXT part
 *                                   'after': if charPosition is mid-word, word goes to PREVIOUS part
 *                                   Default 'after' preserves original behavior (used by splitBlock).
 * @returns {number} Index at which to split words (words before index go to first part)
 */
const findWordSplitIndex = (block, charPosition, mode = 'after') => {
  if (!block.words || !Array.isArray(block.words) || block.words.length === 0) {
    return 0;
  }
  const reconstructedText = block.words
    .map((word, idx) => {
      const wordText = (word.word || word.raw || '').trim();
      return idx === 0 ? wordText : ` ${wordText}`;
    })
    .join('');
  const normalizedBlockText = block.text.replace(/\s+/g, ' ').trim();
  const normalizedReconstructed = reconstructedText.replace(/\s+/g, ' ').trim();
  if (normalizedBlockText !== normalizedReconstructed &&
      Math.abs(normalizedBlockText.length - normalizedReconstructed.length) >= 3) {
    return 0;
  }
  let charCount = 0;
  for (let i = 0; i < block.words.length; i++) {
    const word = block.words[i];
    const wordText = (word.word || word.raw || '').trim();
    const wordStart = charCount;
    const wordEnd = charCount + wordText.length;
    if (charPosition <= wordStart) {
      return i;
    }
    if (charPosition <= wordEnd) {
      // charPosition is within this word:
      // 'before' mode (for selection start) → include word in NEXT part (selected)
      // 'after' mode (for selection end) → include word in PREVIOUS part (selected)
      return mode === 'before' ? i : i + 1;
    }
    charCount = wordEnd + 1;
  }
  return block.words.length;
};

/**
 * Split a block by selection range into three blocks: before, selected (assigned to newSpeaker), after.
 * Before and after keep the original speaker; selected gets newSpeaker.
 * @param {Object} block - Block to split
 * @param {number} selectionStart - Start character offset of selection in block.text
 * @param {number} selectionEnd - End character offset of selection in block.text
 * @param {string} newSpeaker - Speaker to assign to the selected middle part
 * @returns {Array} [beforeBlock, selectedBlock, afterBlock]
 */
export const splitBlockBySelection = (block, selectionStart, selectionEnd, newSpeaker) => {
  const textBefore = block.text.substring(0, selectionStart).trim();
  const textSelected = block.text.substring(selectionStart, selectionEnd).trim();
  const textAfter = block.text.substring(selectionEnd).trim();

  let timeBeforeEnd = (block.start + block.end) / 2;
  let timeSelectedStart = timeBeforeEnd;
  let timeSelectedEnd = (block.start + block.end) / 2;
  let timeAfterStart = timeSelectedEnd;

  let wordsBefore = undefined;
  let wordsSelected = undefined;
  let wordsAfter = undefined;

  if (block.words && Array.isArray(block.words) && block.words.length > 0) {
    const reconstructedText = block.words
      .map((word, idx) => {
        const wordText = (word.word || word.raw || '').trim();
        return idx === 0 ? wordText : ` ${wordText}`;
      })
      .join('');
    const normalizedBlockText = block.text.replace(/\s+/g, ' ').trim();
    const normalizedReconstructed = reconstructedText.replace(/\s+/g, ' ').trim();
    if (normalizedBlockText === normalizedReconstructed ||
        normalizedBlockText.length === 0 ||
        Math.abs(normalizedBlockText.length - normalizedReconstructed.length) < 3) {
      const iStart = findWordSplitIndex(block, selectionStart, 'before');
      const iEnd = findWordSplitIndex(block, selectionEnd, 'after');
      const iBeforeEnd = Math.min(iStart, block.words.length);
      const iSelectedEnd = Math.min(iEnd, block.words.length);
      wordsBefore = block.words.slice(0, iBeforeEnd);
      wordsSelected = block.words.slice(iBeforeEnd, iSelectedEnd);
      wordsAfter = block.words.slice(iSelectedEnd);
      if (wordsBefore.length > 0) {
        const last = wordsBefore[wordsBefore.length - 1];
        if (last.end != null) timeBeforeEnd = last.end;
      }
      if (wordsSelected.length > 0) {
        const first = wordsSelected[0];
        const last = wordsSelected[wordsSelected.length - 1];
        if (first.start != null) timeSelectedStart = first.start;
        if (last.end != null) {
          timeSelectedEnd = last.end;
          timeAfterStart = last.end;
        }
      }
      if (wordsAfter.length > 0) {
        const first = wordsAfter[0];
        if (first.start != null) timeAfterStart = first.start;
      }
    }
  }

  const beforeBlock = {
    ...block,
    text: textBefore,
    end: timeBeforeEnd,
    words: wordsBefore && wordsBefore.length > 0 ? wordsBefore : undefined,
    metadata: {
      ...block.metadata,
      isEdited: true,
      lastModified: new Date().toISOString(),
    },
  };

  const selectedBlock = {
    ...block,
    id: generateId(),
    text: textSelected,
    start: timeSelectedStart,
    end: timeSelectedEnd,
    words: wordsSelected && wordsSelected.length > 0 ? wordsSelected : undefined,
    speaker: newSpeaker,
    metadata: {
      ...block.metadata,
      isEdited: true,
      lastModified: new Date().toISOString(),
    },
  };

  const afterBlock = {
    ...block,
    id: generateId(),
    text: textAfter,
    start: timeAfterStart,
    words: wordsAfter && wordsAfter.length > 0 ? wordsAfter : undefined,
    speaker: block.speaker,
    metadata: {
      ...block.metadata,
      isEdited: true,
      lastModified: new Date().toISOString(),
    },
  };

  // Return only non-empty blocks (avoid empty before/after blocks)
  const result = [];
  if (textBefore.length > 0) result.push(beforeBlock);
  result.push(selectedBlock);
  if (textAfter.length > 0) result.push(afterBlock);
  return result;
};

/**
 * Merge two consecutive blocks
 * @param {Object} block1 - First block
 * @param {Object} block2 - Second block
 * @returns {Object} Merged block
 */
export const mergeBlocks = (block1, block2) => {
  // Merge words arrays from both blocks
  const allWords = [];
  if (block1.words && Array.isArray(block1.words) && block1.words.length > 0) {
    allWords.push(...block1.words);
  }
  if (block2.words && Array.isArray(block2.words) && block2.words.length > 0) {
    allWords.push(...block2.words);
  }
  
  return {
    ...block1,
    text: `${block1.text} ${block2.text}`.trim(),
    end: block2.end,
    words: allWords.length > 0 ? allWords : undefined, // Merge all word-level data
    metadata: {
      ...block1.metadata,
      isEdited: true,
      lastModified: new Date().toISOString(),
    },
  };
};

/**
 * Reorder blocks (move block from one position to another)
 * @param {Array} blocks - Array of blocks
 * @param {number} fromIndex - Source index
 * @param {number} toIndex - Destination index
 * @returns {Array} Reordered blocks array
 */
export const reorderBlocks = (blocks, fromIndex, toIndex) => {
  const result = Array.from(blocks);
  const [removed] = result.splice(fromIndex, 1);
  result.splice(toIndex, 0, removed);

  // Update IDs to maintain order
  return result.map((block, index) => ({
    ...block,
    metadata: {
      ...block.metadata,
      isEdited: true,
      lastModified: new Date().toISOString(),
    },
  }));
};

/**
 * Delete a block
 * @param {Array} blocks - Array of blocks
 * @param {string} blockId - ID of block to delete
 * @returns {Array} Blocks array without deleted block
 */
export const deleteBlock = (blocks, blockId) => {
  return blocks.filter((block) => block.id !== blockId);
};

/**
 * Duplicate a block
 * @param {Object} block - Block to duplicate
 * @returns {Object} Duplicated block with new ID
 */
export const duplicateBlock = (block) => {
  return {
    ...block,
    id: generateId(),
    text: `${block.text} (copy)`,
    metadata: {
      ...block.metadata,
      isEdited: true,
      lastModified: new Date().toISOString(),
    },
  };
};

/**
 * Update block text
 * @param {Array} blocks - Array of blocks
 * @param {string} blockId - ID of block to update
 * @param {string} newText - New text content
 * @returns {Array} Updated blocks array
 */
export const updateBlockText = (blocks, blockId, newText, syncedWords = null) => {
  return blocks.map((block) =>
    block.id === blockId
      ? {
          ...block,
          text: newText,
          ...(syncedWords && { words: syncedWords }), // Update words if provided
          metadata: {
            ...block.metadata,
            isEdited: true,
            lastModified: new Date().toISOString(),
          },
        }
      : block
  );
};

/**
 * Update block speaker
 * @param {Array} blocks - Array of blocks
 * @param {string} blockId - ID of block to update
 * @param {string} newSpeaker - New speaker name
 * @returns {Array} Updated blocks array
 */
export const updateBlockSpeaker = (blocks, blockId, newSpeaker) => {
  return blocks.map((block) =>
    block.id === blockId
      ? {
          ...block,
          speaker: newSpeaker,
          metadata: {
            ...block.metadata,
            isEdited: true,
            lastModified: new Date().toISOString(),
          },
        }
      : block
  );
};

/**
 * Update multiple blocks' speaker (bulk operation)
 * @param {Array} blocks - Array of blocks
 * @param {Array} blockIds - Array of block IDs to update
 * @param {string} newSpeaker - New speaker name
 * @returns {Array} Updated blocks array
 */
export const updateMultipleBlocksSpeaker = (blocks, blockIds, newSpeaker) => {
  const idsSet = new Set(blockIds);
  return blocks.map((block) =>
    idsSet.has(block.id)
      ? {
          ...block,
          speaker: newSpeaker,
          metadata: {
            ...block.metadata,
            isEdited: true,
            lastModified: new Date().toISOString(),
          },
        }
      : block
  );
};

/**
 * Get list of unique speakers from blocks
 * @param {Array} blocks - Array of blocks
 * @returns {Array} Array of unique speaker names with counts
 */
export const getSpeakersList = (blocks) => {
  if (!blocks || !Array.isArray(blocks)) {
    return [];
  }

  const speakersMap = new Map();

  blocks.forEach((block) => {
    const speaker = block.speaker || 'UNKNOWN';
    speakersMap.set(speaker, (speakersMap.get(speaker) || 0) + 1);
  });

  return Array.from(speakersMap.entries())
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count);
};

/**
 * Find block by ID
 * @param {Array} blocks - Array of blocks
 * @param {string} blockId - Block ID to find
 * @returns {Object|null} Found block or null
 */
export const findBlockById = (blocks, blockId) => {
  return blocks.find((block) => block.id === blockId) || null;
};

/**
 * Find block index by ID
 * @param {Array} blocks - Array of blocks
 * @param {string} blockId - Block ID to find
 * @returns {number} Block index or -1 if not found
 */
export const findBlockIndex = (blocks, blockId) => {
  return blocks.findIndex((block) => block.id === blockId);
};

/**
 * Get blocks in time range
 * @param {Array} blocks - Array of blocks
 * @param {number} startTime - Start time in seconds
 * @param {number} endTime - End time in seconds
 * @returns {Array} Blocks within time range
 */
export const getBlocksInTimeRange = (blocks, startTime, endTime) => {
  return blocks.filter(
    (block) =>
      (block.start >= startTime && block.start < endTime) ||
      (block.end > startTime && block.end <= endTime) ||
      (block.start <= startTime && block.end >= endTime)
  );
};

/**
 * Search blocks by text
 * @param {Array} blocks - Array of blocks
 * @param {string} query - Search query
 * @param {boolean} caseSensitive - Whether search should be case-sensitive
 * @returns {Array} Matching blocks with match info
 */
export const searchBlocks = (blocks, query, caseSensitive = false) => {
  if (!query) return [];

  const searchQuery = caseSensitive ? query : query.toLowerCase();

  return blocks
    .map((block) => {
      const text = caseSensitive ? block.text : block.text.toLowerCase();
      const index = text.indexOf(searchQuery);

      if (index !== -1) {
        return {
          block,
          matchIndex: index,
          matchLength: query.length,
        };
      }
      return null;
    })
    .filter(Boolean);
};

/**
 * Replace text in blocks
 * @param {Array} blocks - Array of blocks
 * @param {string} findText - Text to find
 * @param {string} replaceText - Text to replace with
 * @param {boolean} replaceAll - Replace all occurrences or just first
 * @param {boolean} caseSensitive - Whether search should be case-sensitive
 * @returns {Array} Updated blocks array
 */
export const replaceTextInBlocks = (
  blocks,
  findText,
  replaceText,
  replaceAll = false,
  caseSensitive = false
) => {
  if (!findText) return blocks;

  let hasReplaced = false;

  return blocks.map((block) => {
    if (!replaceAll && hasReplaced) {
      return block;
    }

    const regex = createSimpleRegex(findText, caseSensitive);

    const newText = block.text.replace(regex, (match) => {
      hasReplaced = true;
      return replaceText;
    });

    if (newText !== block.text) {
      const updatedWords = Array.isArray(block.words) && block.words.length > 0
        ? syncWordsWithText(newText, block.words, block.start, block.end)
        : block.words;

      return {
        ...block,
        text: newText,
        words: updatedWords,
        metadata: {
          ...block.metadata,
          isEdited: true,
          lastModified: new Date().toISOString(),
        },
      };
    }

    return block;
  });
};

/**
 * Validate blocks array
 * @param {Array} blocks - Array of blocks to validate
 * @returns {Object} Validation result with errors array
 */
export const validateBlocks = (blocks) => {
  const errors = [];

  if (!Array.isArray(blocks)) {
    errors.push('Blocks must be an array');
    return { valid: false, errors };
  }

  blocks.forEach((block, index) => {
    if (!block.id) {
      errors.push(`Block at index ${index} missing ID`);
    }
    if (typeof block.text !== 'string') {
      errors.push(`Block ${block.id} has invalid text`);
    }
    if (typeof block.start !== 'number' || block.start < 0) {
      errors.push(`Block ${block.id} has invalid start time`);
    }
    if (typeof block.end !== 'number' || block.end < 0) {
      errors.push(`Block ${block.id} has invalid end time`);
    }
    if (block.start >= block.end) {
      errors.push(`Block ${block.id} has start time >= end time`);
    }
  });

  return {
    valid: errors.length === 0,
    errors,
  };
};

/**
 * Replace all occurrences of a text in all blocks
 * @param {Array} blocks - Array of blocks
 * @param {string} searchText - Text to search for
 * @param {string} replaceText - Text to replace with
 * @returns {Object} Updated blocks and count of replacements
 */
export const replaceAllInBlocks = (blocks, searchText, replaceText) => {
  if (!searchText || !blocks || blocks.length === 0) {
    return { blocks, count: 0 };
  }

  let replacementCount = 0;
  const regex = createWordBoundaryRegex(searchText, false);

  const updatedBlocks = blocks.map((block) => {
    const originalText = block.text ?? '';
    // Reset regex lastIndex defensively (global regex has stateful lastIndex
    // that could leak across blocks if the engine reuses the object).
    regex.lastIndex = 0;
    const newText = originalText.replace(regex, () => {
      replacementCount++;
      return replaceText;
    });

    if (newText !== originalText) {
      // Regenerate words array from the new text so the Slate editor (which
      // renders from block.words when word-level data is present) reflects
      // the change. Per-word replacement is insufficient for multi-word
      // phrases that span multiple word tokens.
      const updatedWords = Array.isArray(block.words) && block.words.length > 0
        ? syncWordsWithText(newText, block.words, block.start, block.end)
        : block.words;

      return {
        ...block,
        text: newText,
        words: updatedWords,
        metadata: {
          ...block.metadata,
          isEdited: true,
          lastModified: new Date().toISOString(),
        },
      };
    }

    return block;
  });

  return {
    blocks: updatedBlocks,
    count: replacementCount,
  };
};

/**
 * Count occurrences of a text in all blocks
 * @param {Array} blocks - Array of blocks
 * @param {string} searchText - Text to search for
 * @returns {number} Number of occurrences
 */
export const countOccurrences = (blocks, searchText) => {
  if (!searchText || !blocks || blocks.length === 0) {
    return 0;
  }

  const regex = createWordBoundaryRegex(searchText, false);

  let count = 0;
  blocks.forEach((block) => {
    const text = block.text ?? '';
    regex.lastIndex = 0;
    const matches = text.match(regex);
    if (matches) count += matches.length;
  });

  return count;
};

/**
 * Group consecutive blocks by same speaker into chunks of 8-10 sentences
 * @param {Array} blocks - Original blocks array
 * @returns {Array} Grouped blocks
 */
export const groupBlocksBySpeaker = (blocks) => {
  if (!blocks || blocks.length === 0) {
    return [];
  }

  // Count sentences in text (., ?, !)
  const countSentences = (text) => {
    if (!text) return 0;
    return (text.match(/[.?!]/g) || []).length;
  };
  
  // Merge multiple blocks into one
  const mergeBlocks = (blocksToMerge) => {
    if (blocksToMerge.length === 0) return null;
    if (blocksToMerge.length === 1) return blocksToMerge[0];
    
    const texts = blocksToMerge.map(b => b.text.trim()).filter(t => t);
    
    // Merge all words arrays from all blocks
    const allWords = [];
    for (const block of blocksToMerge) {
      if (block.words && Array.isArray(block.words) && block.words.length > 0) {
        allWords.push(...block.words);
      }
    }
    
    return {
      ...blocksToMerge[0],
      text: texts.join(' '),
      end: blocksToMerge[blocksToMerge.length - 1].end,
      words: allWords.length > 0 ? allWords : undefined, // ✅ Merge all word-level data
      metadata: {
        ...blocksToMerge[0].metadata,
        isGrouped: true,
        originalBlockIds: blocksToMerge.map(b => b.id)
      }
    };
  };

  const grouped = [];
  let currentGroup = [];
  let sentenceCount = 0;
  
  for (const block of blocks) {
    const blockSentences = countSentences(block.text);
    
    // Start new group if speaker changes
    if (currentGroup.length > 0 && block.speaker !== currentGroup[0].speaker) {
      const merged = mergeBlocks(currentGroup);
      if (merged) grouped.push(merged);
      currentGroup = [block];
      sentenceCount = blockSentences;
      continue;
    }
    
    // Add to current group
    currentGroup.push(block);
    sentenceCount += blockSentences;
    
    // Finalize group if reached 8-10 sentences (use 8 as threshold)
    if (sentenceCount >= 8) {
      const merged = mergeBlocks(currentGroup);
      if (merged) grouped.push(merged);
      currentGroup = [];
      sentenceCount = 0;
    }
  }
  
  // Handle remaining blocks
  if (currentGroup.length > 0) {
    const merged = mergeBlocks(currentGroup);
    if (merged) grouped.push(merged);
  }
  
  return grouped;
};


